import {API_BASE} from "./config";
import {apiFetch, apiFetchBlob, ApiError} from "./client";
import type {PayinPartState, PayinRequisites} from "../generated/openapi-contract";

export interface PortalCurrentRequisites {
    parts?: PayinPartState[];
    paymentId: string;
    attemptId: string;
    requisitesVersion: number;
    kind: "card" | "sbp";
    cardNumber?: string;
    cardHolder?: string;
    bankName: string;
    phone?: string;
    recipient?: string;
}

export interface PortalReceipt {
	partRevision?: number;
    partNumber?: number;
    receiptId: string;
    orderId: string;
    paymentId: string;
    attemptId: string;
    requisitesVersion: number;
    authorId: string;
    authorRole: string;
    createdAt: string;
}

export type PortalReceiptAudience = "merchant" | "operator";

function portalReceiptBase(orderId: string, audience: PortalReceiptAudience): string {
    const prefix = audience === "merchant" ? "/merchant/me" : "";
    return `${prefix}/orders/${encodeURIComponent(orderId)}/portal-receipts`;
}

export function getCurrentPortalRequisites(token: string | null, orderId: string, signal?: AbortSignal): Promise<PortalCurrentRequisites> {
    return apiFetch(`/merchant/me/orders/${encodeURIComponent(orderId)}/requisites/current`, token, {signal});
}

export function listPortalReceipts(token: string | null, orderId: string, audience: PortalReceiptAudience = "merchant", signal?: AbortSignal): Promise<{items: PortalReceipt[]}> {
    return apiFetch(portalReceiptBase(orderId, audience), token, {signal});
}

export type ReceiptTarget = Pick<PortalReceipt, "paymentId" | "attemptId" | "requisitesVersion" | "partNumber" | "partRevision">;

export async function uploadPortalReceipt(token: string | null, orderId: string, target: ReceiptTarget, file: File): Promise<PortalReceipt> {
    if (!token) throw new ApiError(401, "Unauthorized");
    const form = new FormData(); form.append("file", file);
    const query = new URLSearchParams({paymentId: target.paymentId, attemptId: target.attemptId, requisitesVersion: String(target.requisitesVersion)});
    if (target.partNumber !== undefined) query.set("partNumber", String(target.partNumber));
    if (target.partRevision !== undefined) query.set("partRevision", String(target.partRevision));
    const res = await fetch(`${API_BASE}/merchant/me/orders/${encodeURIComponent(orderId)}/portal-receipts?${query}`, {method: "POST", body: form, headers: {Authorization: `Bearer ${token}`} });
    if (!res.ok) throw new ApiError(res.status, `API error: ${res.status}`, await res.text().catch(() => ""));
    return res.json();
}

export function confirmPayinPart(token: string | null, paymentId: string, partNumber: number, requisitesVersion: number, receiptId: string, admin = false, partRevision = 1): Promise<{confirmed: boolean}> {
    return apiFetch(`/payments/${encodeURIComponent(paymentId)}/payin-parts/${partNumber}/${admin ? "review" : "confirm"}`, token, {method: "POST", body: JSON.stringify({requisitesVersion, receiptId, partRevision})});
}

export function replacePayinPart(token: string | null, paymentId: string, partNumber: number, request: {requestId: string; requisitesVersion: number; partRevision: number; requisites: PayinRequisites}): Promise<{replaced: boolean}> {
    return apiFetch(`/payments/${encodeURIComponent(paymentId)}/payin-parts/${partNumber}/replace`, token, {method: "POST", body: JSON.stringify(request)});
}

export async function uploadPayinPartFailure(token: string | null, orderId: string, target: Pick<PortalReceipt, "paymentId" | "attemptId" | "requisitesVersion">, partNumber: number, file: File, reason: string, note: string, partRevision = 1): Promise<unknown> {
    if (!token) throw new ApiError(401, "Unauthorized");
    const form = new FormData();
    form.append("file", file); form.append("reason", reason); form.append("note", note); form.append("noDebitConfirmed", "true");
    const query = new URLSearchParams({paymentId: target.paymentId, attemptId: target.attemptId, requisitesVersion: String(target.requisitesVersion), partNumber: String(partNumber)});
    query.set("partRevision", String(partRevision));
    const res = await fetch(`${API_BASE}/merchant/me/orders/${encodeURIComponent(orderId)}/payin-part-failures?${query}`, {method: "POST", body: form, headers: {Authorization: `Bearer ${token}`}});
    if (!res.ok) throw new ApiError(res.status, `API error: ${res.status}`, await res.text().catch(() => ""));
    return res.json();
}

export function downloadPayinPartFailure(token: string | null, orderId: string, id: string, audience: PortalReceiptAudience = "merchant"): Promise<Blob> {
    return apiFetchBlob(`${audience === "merchant" ? "/merchant/me" : ""}/orders/${encodeURIComponent(orderId)}/payin-part-failures/${encodeURIComponent(id)}`, token);
}

export function downloadPortalReceipt(token: string | null, orderId: string, receiptId: string, audience: PortalReceiptAudience = "merchant"): Promise<Blob> {
    return apiFetchBlob(`${portalReceiptBase(orderId, audience)}/${encodeURIComponent(receiptId)}`, token);
}
